/**
 * Captura PCM mono de um MediaStream via AudioWorklet (universal: Chromium,
 * Safari, Firefox). Acumula blocos Float32 no thread principal; ao parar,
 * devolve um único Float32Array + sampleRate, pronto para o encoder AAC do
 * Path B (recurso m4a-recording).
 */

const WORKLET_NAME = 'pcm-capture';
const workletUrl = new URL('./pcm-capture.worklet.js', import.meta.url);

export interface PcmResult {
    samples: Float32Array;
    sampleRate: number;
}

export class PcmRecorder {
    private node: AudioWorkletNode | null = null;
    private zeroGain: GainNode | null = null;
    private chunks: Float32Array[] = [];
    private total = 0;

    constructor(
        private readonly ctx: AudioContext,
        private readonly source: MediaStreamAudioSourceNode,
    ) {}

    /** Registra o worklet, conecta a fonte e começa a acumular. */
    async start(): Promise<void> {
        if (this.ctx.state === 'suspended') {
            // iOS Safari: o contexto precisa ser retomado dentro do gesto do usuário.
            await this.ctx.resume();
        }
        await this.ctx.audioWorklet.addModule(workletUrl);

        this.node = new AudioWorkletNode(this.ctx, WORKLET_NAME, { numberOfInputs: 1, numberOfOutputs: 1 });
        this.node.port.onmessage = (e: MessageEvent<Float32Array>) => {
            const buf = e.data;
            this.chunks.push(buf);
            this.total += buf.length;
        };

        // Mantém o grafo "puxando" o áudio sem reproduzir nada (ganho 0 → mudo).
        this.zeroGain = this.ctx.createGain();
        this.zeroGain.gain.value = 0;

        this.source.connect(this.node);
        this.node.connect(this.zeroGain);
        this.zeroGain.connect(this.ctx.destination);
    }

    /** PCM acumulado até agora, sem parar a captura. */
    snapshot(): PcmResult {
        const samples = new Float32Array(this.total);
        let offset = 0;
        for (const c of this.chunks) {
            samples.set(c, offset);
            offset += c.length;
        }
        return { samples, sampleRate: this.ctx.sampleRate };
    }

    /** Para a captura e devolve o PCM mono concatenado. */
    stop(): PcmResult {
        const result = this.snapshot();
        this.disconnect();
        return result;
    }

    /** Descarta a captura (cancelamento) sem alocar o resultado. */
    dispose(): void {
        this.disconnect();
        this.chunks = [];
        this.total = 0;
    }

    private disconnect(): void {
        if (this.node) {
            this.node.port.onmessage = null;
            try { this.source.disconnect(this.node); } catch { /* já desconectado */ }
            try { this.node.disconnect(); } catch { /* noop */ }
            this.node = null;
        }
        if (this.zeroGain) {
            try { this.zeroGain.disconnect(); } catch { /* noop */ }
            this.zeroGain = null;
        }
    }
}
